import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../Button';
import { ReferralProgress } from './ReferralProgress';
import { SectionWrapper } from '../SectionWrapper';
import { GradientTitle } from '../GradientTitle';
import type { ReferralStats } from '../../types/referral';

interface ReferralSectionProps {
  referralStats: ReferralStats;
  referralCode: string;
  onCopyReferralCode: () => void;
}

export const ReferralSection: React.FC<ReferralSectionProps> = ({
  referralStats,
  referralCode,
  onCopyReferralCode
}) => {
  const { t } = useTranslation();

  return (
    <SectionWrapper>
      <div className="flex justify-between items-start">
        <GradientTitle
          title={t('PROFILE_SCREEN.REFERRAL.TITLE')}
          caption={t('PROFILE_SCREEN.REFERRAL.CAPTION')}
        />
        <div className="inline-flex flex-col items-center px-4 py-2 bg-gradient-pink rounded-xl shadow-[0_4px_12px_rgba(255,0,128,0.2)]">
          <span className="text-[11px] text-white/80 uppercase tracking-[0.5px]">
            {t('PROFILE_SCREEN.REFERRAL.RANK')}
          </span>
          <span className="text-base font-semibold text-white">
            {referralStats?.current_rank || 'Bronze'}
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-3 my-4">
          <div className="bg-tg-secondary-bg p-4 rounded-xl flex flex-col gap-1 border border-pink-500/10 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)]">
            <span className="text-[13px] text-tg-hint">
              {t('PROFILE_SCREEN.REFERRAL.TOTAL_REFERRALS')}
            </span>
            <span 
              className="text-xl font-semibold text-tg-text"
              style={{
                background: 'linear-gradient(135deg, #FF0080, #FF6B00)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              {referralStats?.total_referrals || 0}
            </span>
          </div>
          <div className="bg-tg-secondary-bg p-4 rounded-xl flex flex-col gap-1 border border-pink-500/10 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)]">
            <span className="text-[13px] text-tg-hint">
              {t('PROFILE_SCREEN.REFERRAL.ACTIVE_REFERRALS')}
            </span>
            <span 
              className="text-xl font-semibold text-tg-text"
              style={{
                background: 'linear-gradient(135deg, #FF0080, #FF6B00)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              {referralStats?.active_referrals || 0}
            </span>
          </div>
          <div className="bg-tg-secondary-bg p-4 rounded-xl flex flex-col gap-1 border border-pink-500/10 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)] col-span-2">
            <span className="text-[13px] text-tg-hint">
              {t('PROFILE_SCREEN.REFERRAL.REFERRALS_BY_LEVEL')}
            </span>
            <span 
              className="text-xl font-semibold text-tg-text"
              style={{
                background: 'linear-gradient(135deg, #FF0080, #FF6B00)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              {referralStats?.referrals_by_level?.level1 || 0}/
              {referralStats?.referrals_by_level?.level2 || 0}/
              {referralStats?.referrals_by_level?.level3 || 0}
            </span>
          </div>
        </div>

        <ReferralProgress
          currentPoints={referralStats?.total_earnings || 0}
          nextRankPoints={referralStats?.points_to_next_rank || 0}
        />

        <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-tg-secondary-bg rounded-xl border border-pink-500/10 mt-2">
          <span 
            className="font-mono text-sm font-semibold tracking-[0.5px] flex-1 whitespace-nowrap overflow-hidden text-ellipsis"
            style={{
              background: 'linear-gradient(135deg, #FF0080, #FF6B00)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            {referralCode ? `https://t.me/BigDealAppBot?start=${referralCode}` : 'Loading...'}
          </span>
          <Button 
            onClick={onCopyReferralCode} 
            disabled={!referralCode}
            variant="primary"
            size="small"
          >
            {t('PROFILE_SCREEN.REFERRAL.COPY')}
          </Button>
        </div>
      </div>
    </SectionWrapper>
  );
};

